import type { HTMLAttributes } from 'react'
import { Skeleton } from '../../components/ui/skeleton'
import { KuficSkeleton } from '../kufic/kufic-skeleton'

type FormSkeletonProps = HTMLAttributes<HTMLDivElement> & { fields?: number; labels?: string[] }

const CONTROL_WIDTHS = ['48%', '64%', '38%', '72%', '54%'] as const

/**
 * A form loading: each field a 10px label line over a 36px control, then the
 * footer's two controls at the end, so the form lands where the skeleton was.
 */
export function FormSkeleton({ fields = 6, labels, className, ...props }: FormSkeletonProps) {
  return (
    <div
      {...props}
      data-slot="form-skeleton"
      data-skeleton-region
      role="status"
      aria-busy="true"
      className={['grid gap-4 p-4', className].filter(Boolean).join(' ')}
    >
      {labels
        ? labels.map((label, field) => (
            <div
              // Labels can repeat; the field's position is its stable identity here.
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton fields have no persistent state
              key={field}
              className="grid gap-1.5"
            >
              <span className="text-xs font-medium">{label}</span>
              <div
                data-slot="form-skeleton-control"
                aria-hidden="true"
                className="flex h-9 items-center rounded-control border border-input bg-input-background px-2.5"
              >
                <KuficSkeleton width={CONTROL_WIDTHS[field % CONTROL_WIDTHS.length]} />
              </div>
            </div>
          ))
        : Array.from({ length: fields }, (_, field) => (
            <div
              // Placeholder fields have no stable identity and never reorder.
              // biome-ignore lint/suspicious/noArrayIndexKey: placeholder fields have no identity
              key={field}
              className="grid gap-1.5"
            >
              <Skeleton className="h-2.5 w-28" />
              <Skeleton className="h-control w-full" />
            </div>
          ))}
      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Skeleton className="h-control w-20" />
        <Skeleton className="h-control w-24" />
      </div>
    </div>
  )
}
